import React from "react";
import { usePublicLang } from "../../../config/i18n/public";
import SteffiDevice from "../../Steffi/components/SteffiDevice";

/** Screenshot-style strip with two cards + device + controls */
export default function SpeechCardsWithDevice() {
  const { copy, lang } = usePublicLang();
  const c = copy.speech_cards;
  const cards =
    lang === "en"
      ? [
          { text: c.cards[0], src: "/erinnerung_tochter_en.mp3" },
          { text: c.cards[1], src: "/erinnerung_essen_en.mp3" },
        ]
      : [
          { text: c.cards[0], src: "/erinnerung_tochter.mp3" },
          { text: c.cards[1], src: "/erinnerung_essen.mp3" },
        ];

  const audioRef = React.useRef(null);
  const [playingIndex, setPlayingIndex] = React.useState(null);
  const isPlaying = playingIndex !== null && !audioRef.current?.paused;

  React.useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onEnded = () => setPlayingIndex(null);
    const onError = () => setPlayingIndex(null);
    el.addEventListener("ended", onEnded);
    el.addEventListener("error", onError);
    return () => {
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("error", onError);
    };
  }, []);

  const playIndex = async (index) => {
    const el = audioRef.current;
    if (!el) return;
    const requestedSrc = new URL(cards[index].src, window.location.href).href;

    if (el.src === requestedSrc) {
      if (el.paused) {
        await el.play();
        setPlayingIndex(index);
      } else {
        el.pause();
        setPlayingIndex(null);
      }
      return;
    }

    el.src = cards[index].src;
    el.currentTime = 0;
    try {
      await el.play();
      setPlayingIndex(index);
    } catch (e) {
      console.error("Play failed:", e);
    }
  };

  const togglePlayPause = async () => {
    const el = audioRef.current;
    if (!el) return;

    if (!el.src) {
      await playIndex(0);
      return;
    }

    if (el.paused) {
      await el.play();
    } else {
      el.pause();
      setPlayingIndex(null);
    }
  };

  return (
    /* The render is a tight crop where the old PNG carried its own margin, so
       the section has to supply the room the device stands in — the parent
       clips, and the idle float would otherwise cut her foot off. */
    <section className="w-full pb-8">
      <div className="mx-auto w-full max-w-[1480px] px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* LEFT: speech cards */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            {cards.map((c, i) => (
              <SpeechCard
                key={i}
                isActive={playingIndex === i && isPlaying}
                onClick={() => playIndex(i)}
                ariaLabel={playingIndex === i && isPlaying ? c.aria_pause : c.aria_play}
              >
                {c.text}
              </SpeechCard>
            ))}
          </div>

          {/* RIGHT: device + dots + play/pause */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            {/* Her mouth works while the reminder plays, so the device in
                frame is doing the thing the cards describe. */}
            <SteffiDevice
              variant="front"
              speaking={isPlaying}
              alt={c.device_alt}
              className="w-[112px] shrink-0 sm:w-[126px] md:w-[140px] drop-shadow-[0_12px_30px_rgba(0,0,0,0.25)]"
            />
            <div className="flex flex-col items-center gap-4 pb-5 sm:pb-0">
              <DotsVisualizer isPlaying={isPlaying} />
              {false &&
                <RoundBtn
                    ariaLabel={isPlaying ? c.aria_pause : c.aria_play}
                    onClick={togglePlayPause}
                >
                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                </RoundBtn>
                }
            </div>
          </div>
        </div>
      </div>

      <audio ref={audioRef} preload="metadata" />
    </section>
  );
}

/* ---------- components ---------- */

function SpeechCard({ children, isActive, onClick, ariaLabel }) {
  return (
    <div className="relative rounded-3xl bg-[#98BCEF] text-white font-bold shadow-[0_10px_18px_-6px_rgba(0,0,0,0.35)] px-4 sm:px-6 pt-6 pb-14 w-full sm:w-[360px] max-w-full">
      <p className="text-[18px] sm:text-[20px] leading-snug">{children}</p>
      <button
        type="button"
        onClick={onClick}
        className="absolute left-2 bottom-2 grid h-11 w-11 place-items-center rounded-full bg-white text-[#1F2B4D] shadow-md ring-1 ring-black/10"
        aria-label={ariaLabel}
      >
        {isActive ? <PauseIcon /> : <PlayIcon />}
      </button>
    </div>
  );
}

function DotsVisualizer({ isPlaying, count = 15 }) {
  return (
    <>
      <div className="flex items-center gap-2 sm:gap-3">
        {Array.from({ length: count }).map((_, i) => (
          <span
            key={i}
            className="block h-2 w-2 rounded-full bg-black"
            style={{
              animation: `dotPulse 1s ease-in-out ${i * 0.08}s infinite`,
              animationPlayState: isPlaying ? "running" : "paused",
              opacity: 0.3,
            }}
            aria-hidden
          />
        ))}
      </div>
      <style>{`
        @keyframes dotPulse {
          0%, 100% { opacity: .25; transform: translateY(0); }
          50%       { opacity: 1;   transform: translateY(-4px); }
        }
      `}</style>
    </>
  );
}

function RoundBtn({ children, ariaLabel, onClick }) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className="grid h-14 w-14 sm:h-[72px] sm:w-[72px] place-items-center rounded-full bg-[#79B7FF] text-white shadow-[0_10px_22px_-6px_rgba(0,0,0,0.30)]"
    >
      {children}
    </button>
  );
}

/* ---------- icons ---------- */

function PlayIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7-11-7z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
    </svg>
  );
}
